import Sound from './Sound';
import util from './util';

// Decodes audio via the Web Audio API and plays it through the SoundManager graph.
// Can be used directly by passing a fetchFn, or subclassed to override fetchAudioArrayBuffer().
export default class PcmSound extends Sound {
	#fetchFn;
	#stopAc = null;

	constructor(soundManager, cache, fetchFn) {
		super();
		if(!soundManager) throw 'Please specify the sound manager as a parameter';
		if(!cache) throw 'Please specify the sound cache as a parameter';
		this.soundManager = soundManager;
		this.cache = cache;
		this.cacheKey = null; // subclasses or callers may set this to enable caching
		this.pan = 0.0;
		this.#fetchFn = fetchFn ?? null;
	}

	// Override in subclass, or pass fetchFn to constructor.
	fetchAudioArrayBuffer(signal) {
		if(this.#fetchFn) return this.#fetchFn(signal);
		throw new Error('fetchAudioArrayBuffer not implemented');
	}

	setPan(pan) {
		if(pan < -1.0) pan = -1.0;
		if(pan > 1.0) pan = 1.0;
		this.pan = pan;
	}

	setPlaybackRate(rate) {
		this.bufferSource.playbackRate.value = rate;
	}

	load(signal) {
		return this.promise ||= (async () => {
			this._setReadyState('loading');
			this.bufferSource = this.soundManager.audioCtx.createBufferSource();

			const decode = async () => {
				const buf = await this.fetchAudioArrayBuffer(signal);
				const decoded = await this.soundManager.audioCtx.decodeAudioData(buf);
				return decoded;
			};

			let audioBuf;
			try {
				audioBuf = await this.cache.get(this.cacheKey, decode);
			} catch(e) {
				this.promise = null; // allow retry after failure
				this._setReadyState('error', e);
				throw e;
			}

			this.bufferSource.buffer = audioBuf;

			this.targetNode = this.soundManager.audioCtx.createGain();
			this.targetNode.gain.value = this.soundManager.computeNormalizeGain(this.bufferSource.buffer);
			this.targetNode.connect(this.soundManager.targetNode);
			this._setReadyState('ready');
		})();
	}

	play(signal) {
		this.#stopAc = new AbortController();
		const combined = signal
			? util.signalAny([this.#stopAc.signal, signal])
			: this.#stopAc.signal;

		return new Promise((resolve, reject) => {
			if(combined.aborted) return resolve();

			const onEnded = () => {
				this._setReadyState('ended', { target: this, detail: this.bufferSource });
				resolve();
			};

			combined.addEventListener('abort', () => {
				this.bufferSource.removeEventListener('ended', onEnded);
				try {
					this.bufferSource.stop();
					this.bufferSource.disconnect(this.targetNode);
				} catch(e) { /* already stopped or not yet started */ }
				resolve();
			}, { once: true });

			try {
				this.bufferSource.addEventListener('ended', onEnded, { once: true });
				this.bufferSource.connect(this.targetNode);
				this.bufferSource.start(0, 0);
				this._setReadyState('playing', { target: this, detail: this.bufferSource });
			} catch(e) {
				console.error('Playing buffer source', e);
				this._setReadyState('error', { target: this, type: 'playbackError', detail: e, message: 'Playback error' });
				reject(e);
			}
		});
	}

	stop() {
		this.#stopAc?.abort();
	}
}
